import { useState } from 'react';
import { DOC_GROUPS, FIRST_ARTICLE, type DocArticle } from './docs/documents.ts';
import './docs.css';

/**
 * The console's own documentation.
 *
 * A panel, not a page: it renders inside the console frame, which owns the h1
 * and the breadcrumb.
 *
 * Nothing here is fetched. The content is data in `docs/`, so the index, the
 * anchors and the section numbering are derived rather than maintained — a
 * section added to the data appears in the index without anybody remembering
 * to add it, which is the failure this shape exists to prevent.
 *
 * Reading is not a capability (`contracts/src/identity.ts`), so this carries no
 * `requires` and every signed-in person sees all of it. A viewer who cannot run
 * an assessment still needs to know what one does.
 */

const Article = ({ article }: { article: DocArticle }) => (
  <article className="doc-article" aria-labelledby={`doc-${article.id}`}>
    <h2 className="doc-title" id={`doc-${article.id}`}>
      {article.title}
    </h2>
    <p className="lead doc-summary">{article.summary}</p>

    {article.sections.length > 1 && (
      <nav className="doc-contents" aria-labelledby={`doc-contents-${article.id}`}>
        <h3 className="label" id={`doc-contents-${article.id}`}>
          On this page
        </h3>
        <ol className="doc-contents-list">
          {article.sections.map((section) => (
            <li key={section.id}>
              <a className="link" href={`#${section.id}`}>
                {section.heading}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    )}

    {article.sections.map((section, index) => (
      <section key={section.id} id={section.id} className="doc-section">
        <h3 className="doc-heading">
          {/* Positional, and deliberately not stored in the data: a number in
              the content is a number that goes stale when a section moves. */}
          <span className="doc-number" aria-hidden="true">
            {index + 1}.
          </span>
          {section.heading}
        </h3>
        {section.paragraphs.map((paragraph) => (
          <p key={paragraph} className="doc-body">
            {paragraph}
          </p>
        ))}
        {section.code?.map((command) => (
          <pre key={command} className="doc-code">
            <code>{command}</code>
          </pre>
        ))}
        {section.bullets !== undefined && (
          <ul className="doc-bullets">
            {section.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        )}
      </section>
    ))}
  </article>
);

export const Documentation = () => {
  const [openId, setOpenId] = useState<string>(FIRST_ARTICLE.id);
  const open =
    DOC_GROUPS.flatMap((group) => group.articles).find((article) => article.id === openId) ??
    FIRST_ARTICLE;

  return (
    <>
      <p className="lead">
        How this console works, and what its answers mean. Everything here describes what the
        product does today.
      </p>

      <div className="doc-layout">
        <nav className="doc-index" aria-label="Documentation">
          {DOC_GROUPS.map((group) => (
            <div className="doc-index-group" key={group.id}>
              <h2 className="doc-index-heading">{group.label}</h2>
              <ul className="doc-index-list">
                {group.articles.map((article) => (
                  <li key={article.id}>
                    <button
                      type="button"
                      className="doc-index-item"
                      aria-current={article.id === open.id ? 'true' : undefined}
                      onClick={() => {
                        setOpenId(article.id);
                        // The same reset `navigate` performs, and for the same
                        // reason: choosing the eighth article while halfway
                        // down the seventh would otherwise open it at a scroll
                        // position that hides both its title and the index
                        // entry that just changed.
                        window.scrollTo(0, 0);
                      }}
                    >
                      <span className="doc-index-title">{article.title}</span>
                      <span className="doc-index-summary">{article.summary}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="doc-reading">
          <Article article={open} />
        </div>
      </div>
    </>
  );
};
